// src/components/MobileFrame.tsx — root layout for all authenticated routes.
//
// Wraps the child route in a scrollable main area. Renders the fixed
// <BottomNav> only on the four root screens (home / apps / docs / me).
// Inner-app routes (/contacts, future /duty, /alerts, etc.) render inside
// the frame without the bottom nav — they ship their own TopBar with a
// back button.
//
// Active tab is derived from the current pathname so deep links land on
// the correct highlight without any extra state plumbing.

import type { CSSProperties } from "react";
import { Suspense, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { BottomNav, type BottomTabId } from "./ui";
import { ChunkErrorBoundary } from "./ChunkErrorBoundary";
import { useCurrentUser } from "../auth";
import { C, F, layout } from "../theme";

// /docs is intentionally NOT a root path: the rebuilt Documents app (migration
// 079) is a full-screen module with its own sidebar + back-to-Hub, so it renders
// without the bottom nav (like the other rich modules). The Docs bottom-nav tab
// still launches it.
const ROOT_PATHS = new Set<string>(["/", "/apps", "/me"]);

const PATH_BY_TAB: Record<BottomTabId, string> = {
  home: "/",
  apps: "/apps",
  docs: "/docs",
  me: "/me",
};

// Phase 7 -- QM-only blue. Lives here (and in the QM components) because
// `#3D6FA8` is the Quick Message signature; per gotcha #9 it never goes
// into the theme tokens.
const QM_BLUE = "#3D6FA8";

// Phase 8 -- First Aider Callout red. Same rule as QM blue: `#B8362A` is the
// First Aider signature colour and never goes into the theme tokens (gotcha
// #2). The red Home dot WINS over the blue QM dot when both apply (gotcha #5).
const FA_RED = "#B8362A";

// Phase 7 -- emitted by HomePage after Quick Message writes so the bottom-nav
// dot picks up the change without needing a route nav. The QM HomeSection
// fires onUnreadChanged; HomePage dispatches; MobileFrame refetches /api/me.
const ME_REFRESH_EVENT = "nymr-me-refresh";

function tabFromPath(pathname: string): BottomTabId {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/apps")) return "apps";
  if (pathname.startsWith("/docs")) return "docs";
  if (pathname.startsWith("/me")) return "me";
  return "home";
}

export function MobileFrame() {
  const location = useLocation();
  const navigate = useNavigate();
  const { me, refresh } = useCurrentUser();
  const showNav = ROOT_PATHS.has(location.pathname);

  useEffect(() => {
    const handler = () => {
      refresh();
    };
    window.addEventListener(ME_REFRESH_EVENT, handler);
    return () => window.removeEventListener(ME_REFRESH_EVENT, handler);
  }, [refresh]);

  const wrapStyle: CSSProperties = {
    minHeight: "100vh",
    background: C.paper,
    paddingBottom: showNav ? layout.bottomNavH + 24 : 0,
  };

  // Red (active First Aider callout) wins over blue (unread Quick Message) when
  // both apply — gotcha #5.
  const faActive = (me?.inbox?.firstAiderCallouts?.active ?? 0) > 0;
  const homeUnread = (me?.inbox?.quickMessages?.unread ?? 0) > 0;
  const homeDot = faActive ? { color: FA_RED } : homeUnread ? { color: QM_BLUE } : null;

  return (
    <div style={wrapStyle}>
      <ChunkErrorBoundary>
        <Suspense fallback={<div style={{ padding: 48, textAlign: "center", fontFamily: F.body, color: C.inkSoft, fontSize: 14 }}>Loading…</div>}>
          <Outlet />
        </Suspense>
      </ChunkErrorBoundary>
      {showNav && (
        <BottomNav
          active={tabFromPath(location.pathname)}
          onChange={(tab) => navigate(PATH_BY_TAB[tab])}
          dots={homeDot ? { home: homeDot } : undefined}
        />
      )}
    </div>
  );
}
